import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { motion } from "framer-motion";
import { SEO } from "@/components/SEO";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LoadingImage } from "@/components/LoadingImage";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  ArrowsClockwise, Crown, Buildings, CalendarCheck, Camera,
  UserPlus, Trash, Users, User, Plus, X, Heart, DownloadSimple
} from "@phosphor-icons/react";

const LOGO_URL = "/bch-logo.png";
const MAX_FAMILY = 4;

export default function DigitalIdPage() {
  const { token, user } = useAuth();
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFlipped, setIsFlipped] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [showAddFamily, setShowAddFamily] = useState(false);
  const [familyForm, setFamilyForm] = useState({ name: "", relation: "", photo_url: "" });
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const familyPhotoRef = useRef(null);
  const cardFrontRef = useRef(null);
  const cardBackRef = useRef(null);

  useEffect(() => { fetchCardData(); }, []);

  const fetchCardData = async () => {
    try {
      const response = await axios.get(`${API}/id-card/${user?.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCardData(response.data);
      // Fetch family members from profile
      const profileRes = await axios.get(`${API}/public/profile/${user?.id}`);
      setFamilyMembers(profileRes.data.family_members || []);
    } catch (error) {
      console.error("Failed to fetch ID card data:", error);
      toast.error("Failed to load ID card");
    } finally { setLoading(false); }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      return res.data.url;
    } catch { toast.error("Upload failed"); return null; }
    finally { setUploading(false); }
  };

  const handleFamilyPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await uploadImage(file);
    if (url) setFamilyForm(p => ({ ...p, photo_url: url }));
  };

  const handleAddFamily = async () => {
    if (!familyForm.name.trim()) return toast.error("Name is required");
    if (!familyForm.relation.trim()) return toast.error("Relation is required");
    if (!familyForm.photo_url) return toast.error("Photo is required for identity verification");
    const newMember = { id: Date.now().toString(), ...familyForm };
    const updated = [...familyMembers, newMember];
    try {
      await axios.put(`${API}/profile/update`, { family_members: updated }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFamilyMembers(updated);
      setFamilyForm({ name: "", relation: "", photo_url: "" });
      setShowAddFamily(false);
      toast.success("Family member added!");
    } catch { toast.error("Failed to add family member"); }
  };

  const handleDeleteFamily = async (id) => {
    const updated = familyMembers.filter(m => m.id !== id);
    try {
      await axios.put(`${API}/profile/update`, { family_members: updated }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFamilyMembers(updated);
      toast.success("Family member removed");
    } catch { toast.error("Failed to remove"); }
  };

  const getRoleLabel = (role) => {
    const labels = { president: "President", vice_president: "Vice President", secretary: "Secretary", super_admin: "Super Admin", member: "Member" };
    return labels[role] || "Member";
  };

  const downloadPDF = useCallback(async () => {
    if (!cardFrontRef.current || !cardBackRef.current) return;
    setDownloading(true);
    try {
      const captureEl = async (el) => {
        // Clone and fix styles for html2canvas
        const clone = el.cloneNode(true);
        clone.style.position = "fixed";
        clone.style.left = "-9999px";
        clone.style.top = "0";
        clone.style.width = "420px";
        clone.style.height = "260px";
        clone.style.backfaceVisibility = "visible";
        clone.style.transform = "none";
        clone.style.zIndex = "9999";
        clone.style.borderRadius = "12px";
        clone.style.padding = "24px";
        clone.style.display = "flex";

        // Force all child elements to inherit computed styles
        const origChildren = el.querySelectorAll("*");
        const cloneChildren = clone.querySelectorAll("*");
        origChildren.forEach((orig, i) => {
          if (cloneChildren[i]) {
            const cs = window.getComputedStyle(orig);
            const s = cloneChildren[i].style;
            s.display = cs.display;
            s.flexDirection = cs.flexDirection;
            s.alignItems = cs.alignItems;
            s.justifyContent = cs.justifyContent;
            s.gap = cs.gap;
            s.width = cs.width;
            s.height = cs.height;
            s.padding = cs.padding;
            s.margin = cs.margin;
            s.fontSize = cs.fontSize;
            s.fontWeight = cs.fontWeight;
            s.fontFamily = cs.fontFamily;
            s.color = cs.color;
            s.backgroundColor = cs.backgroundColor;
            s.borderRadius = cs.borderRadius;
            s.textTransform = cs.textTransform;
            s.letterSpacing = cs.letterSpacing;
            s.lineHeight = cs.lineHeight;
            s.overflow = cs.overflow;
            s.flexShrink = cs.flexShrink;
            s.flex = cs.flex;
            s.textAlign = cs.textAlign;
            s.position = cs.position;
            s.opacity = cs.opacity;
            s.border = cs.border;
            s.objectFit = cs.objectFit;
          }
        });

        document.body.appendChild(clone);
        const canvas = await html2canvas(clone, { scale: 3, useCORS: true, backgroundColor: null, logging: false });
        document.body.removeChild(clone);
        return canvas;
      };

      const frontCanvas = await captureEl(cardFrontRef.current);
      const backCanvas = await captureEl(cardBackRef.current);

      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: [86, 54] });
      pdf.addImage(frontCanvas.toDataURL("image/png"), "PNG", 0, 0, 86, 54);
      pdf.addPage([86, 54], "landscape");
      pdf.addImage(backCanvas.toDataURL("image/png"), "PNG", 0, 0, 86, 54);

      const fileName = `BCH_ID_Card_${cardData?.full_name?.replace(/\s+/g, "_") || "member"}.pdf`;
      pdf.save(fileName);
      toast.success("ID Card downloaded!");
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Failed to download ID Card");
    } finally {
      setDownloading(false);
    }
  }, [cardData]);

  if (loading) return (
    <>
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-bch-teal border-t-transparent rounded-full animate-spin" />
      </div>
    </>
  );

  return (
    <>
      <SEO title="Digital ID Card" description="Your premium BCH digital identity card with QR code for quick check-ins." path="/id-card" noIndex />
      <div className="space-y-8 animate-fadeInUp">
        <div className="text-center">
          <h1 className="font-display text-4xl text-primary mb-2">Digital ID Card</h1>
          <p className="text-secondary">Click the card to flip and view QR code</p>
        </div>

        {/* ID Card */}
        <div className="flex justify-center">
          <div className="perspective cursor-pointer" onClick={() => setIsFlipped(!isFlipped)}>
            <motion.div
              className="relative w-[420px] h-[260px] preserve-3d"
              animate={{ rotateY: isFlipped ? 180 : 0 }}
              transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
              style={{ transformStyle: "preserve-3d" }}
            >
              {/* Front of Card */}
              <div ref={cardFrontRef} className="absolute inset-0 id-card rounded-xl p-6 backface-hidden" style={{ backfaceVisibility: "hidden" }}>
                <div className="h-full flex flex-col justify-between">
                  <div className="flex items-start justify-between">
                    <img src={LOGO_URL} alt="BCH" className="h-14 w-auto" />
                    <span className="text-bch-gold text-xs font-mono tracking-wider">{cardData?.bch_id}</span>
                  </div>
                  <div className="flex items-end gap-4">
                    {/* Profile Picture on Card */}
                    <div className="w-24 h-24 rounded-xl overflow-hidden border-2 border-bch-gold/30 shrink-0 bg-white/10" data-testid="id-card-photo">
                      {cardData?.profile_picture ? (
                        <img src={cardData.profile_picture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="w-8 h-8 text-white/30" weight="fill" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-white/40 text-xs uppercase tracking-wider mb-0.5">Member</p>
                      <h2 className="font-display text-xl text-bch-gold leading-tight">{cardData?.full_name}</h2>
                      <p className="text-white text-sm">{cardData?.business_name}</p>
                      <p className="text-white/50 text-xs">{cardData?.business_category}</p>
                    </div>
                  </div>
                  <div className="flex items-end justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1.5">
                        <Buildings className="w-3.5 h-3.5 text-white/40" weight="duotone" />
                        <span className="text-white/60 text-xs">{cardData?.chapter_name}</span>
                      </div>
                      {cardData?.role !== "member" && (
                        <div className="flex items-center gap-1.5">
                          <Crown className="w-3.5 h-3.5 text-bch-gold" weight="fill" />
                          <span className="text-bch-gold text-xs">{getRoleLabel(cardData?.role)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    <circle cx="80" cy="20" r="60" fill="none" stroke="#E9C46A" strokeWidth="0.5" />
                    <circle cx="80" cy="20" r="40" fill="none" stroke="#2A9D8F" strokeWidth="0.5" />
                  </svg>
                </div>
              </div>

              {/* Back of Card */}
              <div ref={cardBackRef} className="absolute inset-0 id-card rounded-xl p-6 backface-hidden rotate-y-180" style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}>
                <div className="h-full flex flex-col items-center justify-center">
                  <div className="bg-white p-4 rounded-lg mb-4">
                    <QRCodeSVG value={cardData?.qr_data || "BCH-MEMBER"} size={140} level="H" includeMargin={false} />
                  </div>
                  <p className="text-white/60 text-sm">Scan to verify membership</p>
                  <p className="text-bch-gold font-mono text-sm mt-2">{cardData?.bch_id}</p>
                </div>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                  <img src={LOGO_URL} alt="BCH" className="h-10 w-auto opacity-50" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="flex justify-center gap-4">
          <button onClick={() => setIsFlipped(!isFlipped)} className="flex items-center gap-2 text-secondary hover:text-primary transition-colors">
            <ArrowsClockwise className="w-5 h-5" weight="duotone" />
            <span>Flip Card</span>
          </button>
          <Button onClick={downloadPDF} disabled={downloading}
            className="bg-bch-teal/15 text-bch-teal hover:bg-bch-teal/25" size="sm" data-testid="download-id-card-btn">
            <DownloadSimple className="w-4 h-4 mr-1.5" weight="bold" />
            {downloading ? "Generating..." : "Download PDF"}
          </Button>
        </div>

        {/* ===== FAMILY / NOMINEES SECTION ===== */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-card border border-[var(--border-color)] rounded-2xl overflow-hidden" data-testid="family-section">
            <div className="flex items-center justify-between p-5 border-b border-[var(--border-color)]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-pink-500/15 flex items-center justify-center">
                  <Heart className="w-5 h-5 text-pink-500" weight="fill" />
                </div>
                <div>
                  <h2 className="text-primary font-semibold text-lg">Family & Nominees</h2>
                  <p className="text-muted text-xs">Add up to {MAX_FAMILY} family members who can avail discounts on your behalf</p>
                </div>
              </div>
              {familyMembers.length < MAX_FAMILY && (
                <Button size="sm" onClick={() => setShowAddFamily(true)}
                  className="bg-bch-teal/15 text-bch-teal hover:bg-bch-teal/25" data-testid="add-family-btn">
                  <Plus className="w-4 h-4 mr-1.5" weight="bold" /> Add ({familyMembers.length}/{MAX_FAMILY})
                </Button>
              )}
            </div>

            {/* Add Family Form */}
            {showAddFamily && (
              <div className="p-5 border-b border-[var(--border-color)] bg-surface/50" data-testid="add-family-form">
                <div className="flex gap-4">
                  {/* Photo Upload */}
                  <div className="shrink-0">
                    <div
                      onClick={() => familyPhotoRef.current?.click()}
                      className="w-24 h-24 rounded-xl border-2 border-dashed border-[var(--border-color)] hover:border-bch-teal/50 flex flex-col items-center justify-center cursor-pointer transition-colors overflow-hidden bg-surface"
                      data-testid="family-photo-upload"
                    >
                      {familyForm.photo_url ? (
                        <img src={familyForm.photo_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <>
                          <Camera className="w-6 h-6 text-muted" weight="duotone" />
                          <span className="text-muted text-[10px] mt-1">{uploading ? "Uploading..." : "Photo *"}</span>
                        </>
                      )}
                    </div>
                    <input ref={familyPhotoRef} type="file" accept="image/*" className="hidden" onChange={handleFamilyPhotoUpload} />
                    <p className="text-muted text-[9px] text-center mt-1">400x400 px</p>
                  </div>
                  <div className="flex-1 space-y-3">
                    <Input value={familyForm.name} onChange={e => setFamilyForm(p => ({...p, name: e.target.value}))}
                      placeholder="Full Name *" className="bg-surface border-[var(--border-color)]" data-testid="family-name-input" />
                    <Input value={familyForm.relation} onChange={e => setFamilyForm(p => ({...p, relation: e.target.value}))}
                      placeholder="Relation (e.g. Wife, Son, Brother) *" className="bg-surface border-[var(--border-color)]" data-testid="family-relation-input" />
                    <div className="flex gap-2">
                      <Button onClick={handleAddFamily} disabled={uploading} className="bg-bch-teal hover:bg-bch-teal/80 text-white" size="sm" data-testid="save-family-btn">
                        Save
                      </Button>
                      <Button onClick={() => { setShowAddFamily(false); setFamilyForm({ name: "", relation: "", photo_url: "" }); }}
                        variant="ghost" size="sm" className="text-muted">Cancel</Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Family Members Grid */}
            {familyMembers.length > 0 ? (
              <div className="grid grid-cols-2 gap-px bg-[var(--border-color)]">
                {familyMembers.map(member => (
                  <div key={member.id} className="bg-card p-4 flex items-center gap-4 group" data-testid={`family-member-${member.id}`}>
                    <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0 border border-[var(--border-color)]">
                      {member.photo_url ? (
                        <LoadingImage src={member.photo_url} className="w-16 h-16 rounded-xl" fallback={
                          <div className="w-full h-full bg-pink-500/10 flex items-center justify-center">
                            <User className="w-7 h-7 text-pink-500/50" weight="fill" />
                          </div>
                        } />
                      ) : (
                        <div className="w-full h-full bg-pink-500/10 flex items-center justify-center">
                          <User className="w-7 h-7 text-pink-500/50" weight="fill" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-primary font-semibold text-sm truncate">{member.name}</p>
                      <p className="text-pink-500 text-xs font-medium">{member.relation}</p>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteFamily(member.id); }}
                      className="text-red-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all p-1.5" data-testid={`delete-family-${member.id}`}>
                      <Trash className="w-4 h-4" weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            ) : !showAddFamily ? (
              <div className="p-8 text-center">
                <Users className="w-10 h-10 text-muted/30 mx-auto mb-2" />
                <p className="text-muted text-sm">No family members added</p>
                <p className="text-muted/70 text-xs mt-1">Family members can use your membership at partner stores</p>
              </div>
            ) : null}
          </div>
        </div>

        {/* Card Info */}
        <div className="max-w-2xl mx-auto bg-card border border-[var(--border-color)] rounded-2xl p-6">
          <h3 className="text-primary font-semibold mb-4">About Your ID Card</h3>
          <ul className="space-y-3 text-secondary text-sm">
            <li className="flex items-start gap-2">
              <span className="text-bch-teal">•</span>
              This digital ID card serves as proof of your BCH membership
            </li>
            <li className="flex items-start gap-2">
              <span className="text-bch-teal">•</span>
              The QR code can be scanned at partner stores for discounts
            </li>
            <li className="flex items-start gap-2">
              <span className="text-bch-teal">•</span>
              Your photo and family members' photos will be shown to the store for identity verification
            </li>
            <li className="flex items-start gap-2">
              <span className="text-bch-teal">•</span>
              Family members can avail discounts on your behalf at partner stores
            </li>
            <li className="flex items-start gap-2">
              <span className="text-bch-gold">•</span>
              Keep your membership active to maintain card validity
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}
